/**
 * PhaseStripOverlay — periodization phase ribbon shown ABOVE a chart.
 *
 * Canon v3 раздел график: «Фаза-лента (База / Развитие / Пик / Подводка /
 * Гонка) = HTML-полоса НАД графиком, текущая в акценте, остальные
 * приглушённый инсет (НЕ внутри SVG — иначе каша с месяцами).»
 *
 * Compose as sibling above a `<ResponsiveContainer>`; component sizes
 * each segment proportionally to its duration weight. Currently-active
 * phase is rendered with iris accent + bold mono label; others with
 * the muted inset surface (Tailwind `bg-bg-raised` → var(--bg-raised)).
 */
import type { ReactNode } from "react";

export type PeriodizationPhase = "base" | "build" | "peak" | "taper" | "race";

export interface PhaseSegment {
  phase: PeriodizationPhase;
  /** Display label (already localised). */
  label: string;
  /** Relative weight (duration units) — segments share full width by ratio. */
  weight: number;
  /** True for the currently-active phase. Iris colouring + bold label. */
  active?: boolean;
}

export interface PhaseStripOverlayProps {
  segments: PhaseSegment[];
  /** Optional eyebrow line above the strip (e.g. "ФАЗА БЛОКА"). */
  eyebrow?: ReactNode;
  /** Extra className on outer wrapper for spacing. */
  className?: string;
  testId?: string;
}

const PHASE_LABEL_KEY: Record<PeriodizationPhase, string> = {
  base:  "periodization.phase.base",
  build: "periodization.phase.build",
  peak:  "periodization.phase.peak",
  taper: "periodization.phase.taper",
  race:  "periodization.phase.race",
};

export function getPhaseLabelKey(phase: PeriodizationPhase): string {
  return PHASE_LABEL_KEY[phase];
}

export function PhaseStripOverlay({
  segments,
  eyebrow,
  className = "",
  testId = "phase-strip",
}: PhaseStripOverlayProps) {
  const totalWeight = segments.reduce((s, x) => s + Math.max(0, x.weight), 0) || 1;
  return (
    <div className={`space-y-1 ${className}`.trim()} data-testid={testId}>
      {eyebrow && <div className="indigo-section-eyebrow">{eyebrow}</div>}
      <div className="flex gap-0.5 h-6">
        {segments.map((seg, i) => {
          const widthPct = (Math.max(0, seg.weight) / totalWeight) * 100;
          const active = !!seg.active;
          return (
            <div
              key={`${seg.phase}-${i}`}
              data-testid={`${testId}-${seg.phase}`}
              data-active={active || undefined}
              style={{ width: `${widthPct}%` }}
              className={`min-w-[28px] rounded-pill px-2 flex items-center justify-center text-[9.5px] uppercase tracking-[0.18em] font-mono transition-colors ${
                active
                  ? "bg-accent text-accent-on font-bold"
                  : "bg-bg-raised text-fg-muted font-medium"
              }`}
              title={seg.label}
            >
              <span className="truncate">{seg.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
